/*
 * Restore remembered MyUSE sessions when explicitly requested.
 *
 * This plugin deliberately does not start authentication automatically
 * on page load, browser focus, visibility changes or network changes.
 *
 * Automatic background login attempts can conflict with Logout and can
 * create redirect loops such as:
 *
 *   /dashboard
 *   → /?redirect=%2Fdashboard
 *   → Welcome Back
 *   → /dashboard
 *   → /
 *
 * The Welcome Back dialog or a route guard may call:
 *
 *   app.$resumeMyUseSession()
 *
 * Multiple callers share the same in-flight promise so only one login
 * request is performed at a time.
 */

export default function ({
  store,
  app,
}) {
  if (!process.client) {
    return
  }

  let resumePromise = null
  let initialResumePromise = null
  let lastSuccessfulResumeAt = 0
  let lastFailedResumeAt = 0

  const RESUME_CACHE_MS = 3000
  const FAILURE_COOLDOWN_MS = 3000

  const rememberMeEnabled = () => {
    try {
      return (
        Number(
          store.getters[
            'auth/getRememberMeCookieStatus'
          ]
        ) === 1
      )
    } catch (_) {
      return false
    }
  }

  const getUser = () => {
    try {
      return (
        store.getters[
          'auth/getUser'
        ] || null
      )
    } catch (_) {
      return null
    }
  }

  const getToken = () => {
    try {
      return (
        store.getters[
          'auth/getToken'
        ] || null
      )
    } catch (_) {
      return null
    }
  }

  const getPassword = () => {
    try {
      const password =
        store.getters[
          'auth/getUserPassword'
        ]

      if (password) {
        return password
      }
    } catch (_) {
      // Fall back to the persisted user object below.
    }

    const user =
      getUser()

    return (
      user &&
      user.password
        ? user.password
        : null
    )
  }

  const getMenuItems = () => {
    try {
      const menu =
        store.getters[
          'navigation/getMenuItems'
        ]

      return Array.isArray(menu)
        ? menu
        : []
    } catch (_) {
      return []
    }
  }

  const hasAuthenticatedUser = () => {
    return Boolean(
      getUser() &&
      getToken()
    )
  }

  const hasUsableMenu = () => {
    return (
      getMenuItems().length > 0
    )
  }

  const applyBearerToken = () => {
    const token =
      getToken()

    if (
      !token ||
      !app.$axios
    ) {
      return false
    }

    if (
      typeof app.$axios.setToken ===
        'function'
    ) {
      app.$axios.setToken(
        token,
        'Bearer'
      )

      return true
    }

    if (
      typeof app.$axios.setHeader ===
        'function'
    ) {
      app.$axios.setHeader(
        'Authorization',
        `Bearer ${token}`
      )

      return true
    }

    return false
  }

  const rebuildMenu = async () => {
    try {
      await store.dispatch(
        'navigation/processMenuItems'
      )
    } catch (error) {
      console.error(
        'Could not rebuild the MyUSE menu:',
        error
      )

      return false
    }

    return hasUsableMenu()
  }

  /**
   * Perform one real login using the remembered password.
   *
   * The auth action is responsible for refreshing:
   *
   * - bearer token;
   * - user profile;
   * - processor state;
   * - navigation history;
   * - optional-module state.
   *
   * stayOnDashboard prevents login_user from redirecting while a caller
   * is restoring state before deciding which route should be opened.
   */
  const performRealLogin = async (
    password
  ) => {
    if (!password) {
      return false
    }

    await store.dispatch(
      'auth/login_user',
      {
        password,
        stayOnDashboard: true,
      }
    )

    if (!hasAuthenticatedUser()) {
      return false
    }

    applyBearerToken()

    const menuReady =
      await rebuildMenu()

    return (
      hasAuthenticatedUser() &&
      menuReady
    )
  }

  /**
   * Attempt to restore a remembered session.
   *
   * This function never redirects. The component or route guard that
   * requested restoration decides where the user should go afterward.
   */
  const performResume = async ({
    forceRefresh = false,
  } = {}) => {
    if (!rememberMeEnabled()) {
      return false
    }

    const now =
      Date.now()

    /*
     * Reuse a recently restored live session during normal navigation.
     */
    if (
      !forceRefresh &&
      hasAuthenticatedUser() &&
      hasUsableMenu() &&
      now -
        lastSuccessfulResumeAt <
        RESUME_CACHE_MS
    ) {
      applyBearerToken()

      return true
    }

    /*
     * Avoid rapid repeated failures from several components or guards.
     */
    if (
      !forceRefresh &&
      lastFailedResumeAt > 0 &&
      now -
        lastFailedResumeAt <
        FAILURE_COOLDOWN_MS
    ) {
      return false
    }

    const password =
      getPassword()

    if (password) {
      try {
        const restored =
          await performRealLogin(
            password
          )

        if (restored) {
          lastSuccessfulResumeAt =
            Date.now()

          lastFailedResumeAt = 0

          return true
        }
      } catch (error) {
        console.error(
          'Remembered MyUSE login failed:',
          error
        )
      }
    }

    /*
     * Compatibility fallback for older persisted sessions whose encrypted
     * password is restored by the auth module rather than exposed through
     * getUserPassword.
     */
    try {
      await store.dispatch(
        'auth/tryAutoLoginFromPersisted',
        {
          stayOnDashboard: true,
        }
      )

      if (!hasAuthenticatedUser()) {
        lastFailedResumeAt =
          Date.now()

        return false
      }

      applyBearerToken()

      const menuReady =
        await rebuildMenu()

      const restored =
        hasAuthenticatedUser() &&
        menuReady

      if (restored) {
        lastSuccessfulResumeAt =
          Date.now()

        lastFailedResumeAt = 0

        return true
      }
    } catch (error) {
      console.error(
        'Encrypted MyUSE session restoration failed:',
        error
      )
    }

    lastFailedResumeAt =
      Date.now()

    return false
  }

  /**
   * Public restoration function.
   *
   * Concurrent callers receive the same promise and cannot trigger
   * duplicate login requests.
   */
  const attemptResume = (
    options = {}
  ) => {
    if (!rememberMeEnabled()) {
      return Promise.resolve(
        false
      )
    }

    if (resumePromise) {
      return resumePromise
    }

    resumePromise =
      performResume(options)
        .catch((error) => {
          console.error(
            'MyUSE session restoration failed:',
            error
          )

          lastFailedResumeAt =
            Date.now()

          return false
        })
        .finally(() => {
          resumePromise = null
        })

    return resumePromise
  }

  /*
   * Called explicitly by the Welcome Back dialog or route guards.
   */
  app.$resumeMyUseSession =
    attemptResume

  /*
   * Preserve the existing startup API for components that already call it,
   * but do not invoke it automatically from this plugin.
   */
  app.$myUseInitialResume =
    () => {
      if (!initialResumePromise) {
        initialResumePromise =
          attemptResume({
            forceRefresh: true,
          }).finally(() => {
            /*
             * Allow a later explicit attempt after Logout/Login changes
             * rather than permanently caching a failed startup promise.
             */
            initialResumePromise =
              null
          })
      }

      return initialResumePromise
    }

  /*
   * Intentionally no:
   *
   * - setTimeout startup login;
   * - visibilitychange listener;
   * - window focus listener;
   * - online listener.
   *
   * Session restoration must be initiated by an explicit user action or
   * by a route guard that is actively protecting a requested page.
   */
}